
import { Component, Vue, Watch } from "vue-property-decorator";
import TramiteCardComponent from "@/components/Common/Cards/TramiteCardComponent.vue";
import PortalServiciosHeader from "@/components/Tramites/PortalServiciosHeader.vue";
import categoryService from "@/services/category.service";

@Component({
  components: {
    PortalServiciosHeader,
    TramiteCardComponent
  }
})
export default class PortalServicios extends Vue {
  private selectComposition: Number = 0;
  private showAll: boolean = false;
  private tramites: Array<any> = [];
  private oldTramites: Array<any> = [];
  private categories: Array<any> = [];
  private oldCategories: Array<any> = [];
  private selectedCategoryId: number | null = null;
  private categoryTramitesMap: Map<number, Set<number>> = new Map();
  private maxVisible: number = 5;
  private categoryPages: any = {};
  private async mounted() {
    await Promise.all([
      this.getTramites().catch(() => undefined),
      this.getCategories().catch(() => undefined)
    ]);
  }
  private visibleTramites(categoryId: number) {
    const tramites =
      this.categories.find((cat: any) => cat.id === categoryId)?.tramites || [];
    const start = this.getCategoryPage(categoryId) * this.maxVisible;

    return tramites.slice(start, start + this.maxVisible);
  }
  private getCategoryPage(categoryId: number) {
    return this.categoryPages[categoryId] || 0;
  }
  private categoryTotalPages(categoryId: number) {
    const tramites =
      this.categories.find((cat: any) => cat.id === categoryId)?.tramites || [];

    return Math.ceil(tramites.length / this.maxVisible);
  }
  private canGoPreviousCategoryPage(categoryId: number) {
    return this.getCategoryPage(categoryId) > 0;
  }
  private canGoNextCategoryPage(categoryId: number) {
    return (
      this.getCategoryPage(categoryId) < this.categoryTotalPages(categoryId) - 1
    );
  }
  private changeCategoryPage(categoryId: number, direction: number) {
    const nextPage = Math.min(
      Math.max(this.getCategoryPage(categoryId) + direction, 0),
      this.categoryTotalPages(categoryId) - 1
    );

    this.$set(this.categoryPages, categoryId, nextPage);
  }
  private async getCategories() {
    const response = await categoryService.getCategoriasTramites();
    this.categories = response.map((x: any) => {
      return { image: `${process.env.VUE_APP_MEDIA_URL}${x.img}`, ...x };
    });
    this.oldCategories = this.categories;
    this.categoryTramitesMap = this.buildCategoryTramitesMap(
      this.oldCategories
    );
  }
  private async getTramites() {
    const response = await categoryService.getTramitesByLetter();
    this.tramites = response;
    this.oldTramites = this.tramites;
  }
  private toLink(item: any, target: any) {
    if (item && item != "") {
      window.open(item, target);
    } else {
      return;
    }
  }
  private changeComposition(index: Number) {
    this.selectComposition = index;
    this.applyFilters();
  }

  get categoryOptions() {
    return [
      { label: "Todas las categorías", value: null },
      ...this.oldCategories.map((category: any) => ({
        label: this.formatCategoryName(category.name),
        value: Number(category.id)
      }))
    ];
  }

  get filteredTramites() {
    if (this.showAll && this.tramites.length > 0) {
      return this.tramites; // Mostrar todos los trámites
    } else if (!this.showAll && this.tramites.length > 0) {
      // Filtrar los trámites hasta la letra H
      return this.tramites.filter((letra: any) => letra.letter <= "H");
    } else {
      return [];
    }
  }
  private filtrarCategorias() {
    if (this.selectedCategoryId === null) {
      return this.oldCategories;
    }

    return this.oldCategories.filter(
      (categoria: any) => Number(categoria.id) === this.selectedCategoryId
    );
  }
  private filtrarTramitesLetra() {
    if (this.selectedCategoryId === null) {
      return this.oldTramites;
    }

    const resultado = this.oldTramites
      .map((letra: any) => {
        const tramitesFiltrados = letra.tramites.filter((tramite: any) =>
          this.matchesSelectedCategory(tramite)
        );

        return tramitesFiltrados.length > 0
          ? { ...letra, tramites: tramitesFiltrados }
          : null;
      })
      .filter((letra: any) => letra !== null);

    return resultado;
  }
  @Watch("selectedCategoryId")
  private selectedCategoryWatch() {
    if (this.selectedCategoryId !== null) {
      this.goToCategory(this.selectedCategoryId);
      return;
    }

    this.applyFilters();
  }
  private loadMore() {
    this.showAll = true;
  }
  private loadLess() {
    this.showAll = false;
  }
  private goToTramite(slug: string, scrollToTitle = false) {
    this.$router.push({
      path: `/portal-servicios/${slug}`,
      query: scrollToTitle ? { scrollTo: "title" } : {}
    });
  }
  private goToCategory(categoryId: number) {
    const category = this.oldCategories.find(
      (item: any) => Number(item.id) === Number(categoryId)
    );

    if (!category) {
      return;
    }

    this.$router.push(
      `/portal-servicios/categoria/${category.id}/${this.slugify(
        category.name
      )}`
    );
  }
  private applyFilters() {
    if (this.selectComposition === 0) {
      this.categories = this.filtrarCategorias();
      return;
    }

    this.tramites = this.filtrarTramitesLetra();
  }
  private matchesSelectedCategory(tramite: any) {
    if (this.selectedCategoryId === null) {
      return true;
    }

    const categoryTramites = this.categoryTramitesMap.get(
      this.selectedCategoryId
    );
    if (!categoryTramites) {
      return false;
    }

    return categoryTramites.has(Number(tramite?.id));
  }
  private buildCategoryTramitesMap(categories: Array<any>) {
    const categoryMap = new Map<number, Set<number>>();

    categories.forEach((category: any) => {
      const tramiteIds = new Set<number>();

      (category.tramites || []).forEach((tramite: any) => {
        const tramiteId = Number(tramite?.id);

        if (!Number.isNaN(tramiteId)) {
          tramiteIds.add(tramiteId);
        }
      });

      categoryMap.set(Number(category.id), tramiteIds);
    });

    return categoryMap;
  }
  private formatCategoryName(name: any) {
    return (name || "")
      .toString()
      .replace(/\r\n?/g, "\n")
      .replace(/[ \t]+\n/g, "\n")
      .replace(/\n[ \t]+/g, "\n")
      .trim();
  }
  private slugify(text: any) {
    return (text || "")
      .toString()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "");
  }
}
